import type { SiteLang } from "@/hooks/useLanguage";

/* ───────────────────────────────────────────────────
   Spanish translations for Pharmacovigilance offerings.
   Only the 4 offerings listed in the spreadsheet.
   ─────────────────────────────────────────────────── */

export interface PvOfferingI18n {
  title: string;
  subtitle: string;
  description: string;
  faqItems?: { q: string; a: string }[];
  /* GenericOfferingContent overrides */
  whatTitle: string;
  whatParagraphs: string[];
  whyTitle: string;
  whyBenefits: { title: string; text: string }[];
  helpCards: {
    heading: string;
    intro: string;
    cards: { title: string; text: string }[];
  };
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
}

const pvOfferingsEs: Record<string, PvOfferingI18n> = {
  /* ───── QPPV / Servicios de representación local ───── */
  "qppv-local-representative": {
    title: "QPPV / Servicios de representación local",
    subtitle: "Garantiza el cumplimiento de la farmacovigilancia en todos los territorios",
    description:
      "Gestionar las responsabilidades de farmacovigilancia en múltiples regiones puede ser complejo. En QbD Vigilance, proporcionamos servicios de QPPV y representación local adaptados a las necesidades regulatorias específicas de los TAC en Europa, Reino Unido y más allá.",
    faqItems: [
      {
        q: "¿Qué es un QPPV y por qué es obligatorio?",
        a: "El QPPV (Persona Cualificada Responsable de Farmacovigilancia) es un requisito legal según la normativa de la UE y del Reino Unido. Supervisa el sistema de farmacovigilancia del titular de la autorización de comercialización y garantiza el cumplimiento de las obligaciones de seguridad.",
      },
      {
        q: "¿Ofrecéis servicios de QPPV para la UE y el Reino Unido?",
        a: "Sí, proporcionamos servicios de QPPV tanto para la UE como para el Reino Unido, así como representación local de farmacovigilancia en mercados específicos.",
      },
      {
        q: "¿Qué incluye el servicio de representación local?",
        a: "Nuestros representantes locales gestionan las obligaciones de farmacovigilancia específicas de cada país, incluida la comunicación con las autoridades sanitarias locales, la notificación de eventos adversos y el cumplimiento normativo regional.",
      },
    ],
    whatTitle: "¿Qué son los servicios de QPPV y representación local?",
    whatParagraphs: [
      "El rol de QPPV (Persona Cualificada Responsable de Farmacovigilancia) es <strong class='text-foreground'>obligatorio según la normativa de la UE y del Reino Unido para supervisar la seguridad de los medicamentos</strong>. Además, ciertos países exigen un representante local que gestione las actividades de farmacovigilancia a nivel local. Estos roles garantizan que los TAC cumplan con las obligaciones de farmacovigilancia específicas de cada región.",
      "Nuestros servicios principales incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Servicios de QPPV para la UE</strong>: proporcionamos un contacto central cualificado para supervisar los sistemas de farmacovigilancia y asegurar el cumplimiento en la Unión Europea.</li><li><strong>Servicios de QPPV para el Reino Unido</strong>: cumplimos con los requisitos específicos de supervisión de farmacovigilancia en el Reino Unido.</li><li><strong>Representantes locales de farmacovigilancia</strong>: soporte específico de farmacovigilancia por región para garantizar el cumplimiento en los mercados locales.</li><li><strong>Disponibilidad 24/7</strong>: soporte continuo para la notificación de eventos adversos y consultas regulatorias.</li><li><strong>Preparación para auditorías e inspecciones</strong>: preparación de tus sistemas de farmacovigilancia para auditorías e inspecciones regulatorias.</li><li><strong>Servicio de línea de atención de farmacovigilancia</strong>: soporte farmacológico integral, 24/7, adaptado a tus necesidades — desde la gestión de RAM hasta el cumplimiento regulatorio.</li></ul>",
    ],
    whyTitle: "¿Por qué son esenciales el QPPV y los representantes locales?",
    whyBenefits: [
      { title: "Requisito regulatorio", text: "Cumplimiento de las leyes de farmacovigilancia de la UE, el Reino Unido y a nivel global." },
      { title: "Supervisión centralizada", text: "Garantiza que todos los sistemas de farmacovigilancia se monitoricen y gestionen de forma eficaz." },
      { title: "Experiencia local", text: "Facilita la comunicación con las autoridades sanitarias y los stakeholders locales." },
      { title: "Mitigación de riesgos", text: "Identificación y resolución rápida de problemas de seguridad para proteger a pacientes y productos." },
      { title: "Alcance global", text: "Operaciones de farmacovigilancia fluidas en múltiples territorios." },
      { title: "Respuesta inmediata", text: "Aborda preocupaciones de seguridad y eventos adversos en tiempo real, reduciendo retrasos y mitigando riesgos." },
      { title: "Continuidad operativa", text: "Mantén operaciones de farmacovigilancia sin interrupciones en todas las zonas horarias y regiones." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "En QbD Vigilance, ofrecemos servicios integrales de QPPV local que cumplen con los requisitos regulatorios internacionales y locales:",
      cards: [
        { title: "Servicios dedicados de QPPV para la UE y el Reino Unido", text: "Supervisamos tus sistemas de farmacovigilancia para garantizar el cumplimiento en Europa y el Reino Unido." },
        { title: "Representación local de farmacovigilancia", text: "Proporcionamos experiencia en el mercado local para abordar las obligaciones regulatorias específicas de cada región." },
        { title: "Gestión de eventos adversos 24/7", text: "Garantizamos la notificación puntual y la comunicación con las autoridades sanitarias." },
        { title: "Enlace regulatorio", text: "Actuamos como punto de contacto principal con las autoridades de farmacovigilancia." },
        { title: "Preparación para inspecciones", text: "Preparamos tu organización para auditorías e inspecciones para asegurar el cumplimiento." },
        { title: "Soporte continuo", text: "Disponibilidad permanente para gestionar necesidades urgentes de farmacovigilancia." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Vigilance garantiza:",
    partnerBullets: [
      "Experiencia global — amplia experiencia gestionando responsabilidades de farmacovigilancia en Europa, el Reino Unido y otros mercados internacionales.",
      "Cumplimiento regulatorio — QPPVs y representantes locales dedicados que aseguran el pleno cumplimiento de la normativa de farmacovigilancia.",
      "Soluciones a medida — soporte personalizado que responde a las necesidades únicas de tu organización.",
      "Fiabilidad demostrada — confianza de TAC en todo el mundo por servicios de farmacovigilancia de alta calidad.",
      "Soporte 24/7 — disponibilidad permanente para la notificación de eventos adversos y consultas regulatorias.",
    ],
  },

  /* ───── Servicios de control bibliográfico ───── */
  "literature-monitoring": {
    title: "Servicios de control bibliográfico",
    subtitle: "Detecta señales de seguridad mediante el cribado sistemático de la literatura",
    description:
      "Mantente en cumplimiento con los servicios de control bibliográfico de QbD Group. Detecta señales de seguridad, garantiza la notificación puntual y potencia tu estrategia de vigilancia con búsquedas sistemáticas en publicaciones científicas y bases de datos.",
    faqItems: [
      {
        q: "¿Qué es el control bibliográfico en farmacovigilancia?",
        a: "El control bibliográfico es la revisión sistemática y continua de publicaciones científicas y médicas para identificar información de seguridad relevante sobre medicamentos. Es un requisito regulatorio según las Buenas Prácticas de Farmacovigilancia (GPvP).",
      },
      {
        q: "¿Qué bases de datos cubren vuestras búsquedas?",
        a: "Realizamos búsquedas sistemáticas en múltiples bases de datos, incluidas PubMed, Embase y otras fuentes especializadas relevantes para tu área terapéutica.",
      },
      {
        q: "¿Cómo se gestionan los ICSR identificados en la literatura?",
        a: "Identificamos y procesamos los Informes de Casos Individuales de Seguridad (ICSR) encontrados en la literatura publicada para su envío a las autoridades regulatorias, cumpliendo con los requisitos de la EMA, la FDA y la ICH.",
      },
    ],
    whatTitle: "¿Qué es el control bibliográfico?",
    whatParagraphs: [
      "El control bibliográfico es la <strong class='text-foreground'>revisión sistemática y continua de publicaciones científicas y médicas</strong> para identificar información de seguridad relevante sobre medicamentos. Es un requisito regulatorio según las Buenas Prácticas de Farmacovigilancia (GPvP) y desempeña un papel fundamental en el mantenimiento de un perfil de seguridad actualizado de tus productos.",
      "Nuestros servicios principales incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Búsquedas bibliográficas sistemáticas</strong>: búsquedas regulares y estructuradas en revistas revisadas por pares, bases de datos clínicas y publicaciones científicas utilizando estrategias de búsqueda validadas.</li><li><strong>Identificación de señales de seguridad</strong>: identificación de señales de seguridad nuevas o cambiantes a partir de informes de casos publicados, estudios clínicos y datos epidemiológicos.</li><li><strong>Extracción de ICSR de la literatura</strong>: identificación y procesamiento de Informes de Casos Individuales de Seguridad encontrados en la literatura publicada para su envío regulatorio.</li><li><strong>Gestión de alertas</strong>: configuración de alertas automatizadas para garantizar que no se pase por alto ninguna publicación relevante, con escalamiento puntual de hallazgos críticos.</li><li><strong>Cumplimiento regulatorio</strong>: garantía de que todas las actividades de control bibliográfico cumplen con los requisitos de la EMA, la FDA y la ICH.</li><li><strong>Cobertura de bases de datos</strong>: monitorización de múltiples bases de datos, incluidas PubMed, Embase y otras fuentes especializadas relevantes para tu área terapéutica.</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial el control bibliográfico?",
    whyBenefits: [
      { title: "Obligación regulatoria", text: "El control bibliográfico es obligatorio según el módulo VI de la GVP y los marcos regulatorios globales — su incumplimiento conlleva sanciones y retiradas de productos." },
      { title: "Detección de señales", text: "La literatura publicada es una fuente primaria para identificar nuevas reacciones adversas, interacciones y patrones de uso fuera de indicación." },
      { title: "Seguridad del paciente", text: "La identificación temprana de preocupaciones de seguridad emergentes ayuda a proteger a los pacientes y permite una mitigación de riesgos oportuna." },
      { title: "Perfil de seguridad continuo", text: "Mantiene actualizado el perfil beneficio-riesgo de tu producto a lo largo de su ciclo de vida." },
      { title: "Cobertura global", text: "Las búsquedas sistemáticas en bases de datos internacionales garantizan una supervisión de seguridad integral en todos los mercados." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "Los servicios de control bibliográfico de QbD Group proporcionan soporte integral para mantener tus operaciones de farmacovigilancia en cumplimiento y de forma proactiva:",
      cards: [
        { title: "Estrategias de búsqueda validadas", text: "Diseño y ejecución de protocolos de búsqueda bibliográfica específicos por producto en múltiples bases de datos." },
        { title: "Extracción y notificación de ICSR", text: "Identificación de ICSR válidos en la literatura publicada y su procesamiento para el envío regulatorio." },
        { title: "Gestión puntual de alertas", text: "Configuración de alertas automatizadas y garantía de que no se pase por alto ninguna publicación de seguridad relevante." },
        { title: "Análisis de tendencias", text: "Análisis de tendencias en la literatura para respaldar la detección de señales y las evaluaciones beneficio-riesgo." },
        { title: "Alineación regulatoria", text: "Garantía de que todas las actividades cumplen con el módulo VI de la GVP, la FDA y los requisitos de la ICH." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Elegir QbD Group para el control bibliográfico garantiza:",
    partnerBullets: [
      "Equipo experto de farmacovigilancia — profesionales formados con profundo conocimiento en cribado de literatura de seguridad y extracción de ICSR.",
      "Amplia cobertura de bases de datos — búsquedas sistemáticas en PubMed, Embase y fuentes específicas de cada área terapéutica.",
      "Cumplimiento regulatorio — procesos alineados con el módulo VI de la GVP, la FDA y las directrices de la ICH.",
      "Soluciones escalables — servicios flexibles que se adaptan a la cartera de productos y los requisitos de tu área terapéutica.",
      "Integración fluida — el control bibliográfico se integra directamente en tus operaciones más amplias de farmacovigilancia.",
    ],
  },

  /* ───── Gestión de ICSR ───── */
  "icsr-management": {
    title: "Gestión de ICSR",
    subtitle: "Optimiza la notificación de seguridad para tus ensayos clínicos y productos comercializados",
    description:
      "Gestionar los informes de casos de seguridad de forma eficaz es la piedra angular de la farmacovigilancia y la supervisión de ensayos clínicos. En QbD Group, nuestros servicios especializados de gestión de ICSR garantizan la recopilación, el procesamiento y la notificación puntual de la información de seguridad.",
    faqItems: [
      {
        q: "¿Qué es la gestión de ICSR?",
        a: "La gestión de ICSR abarca la recopilación, evaluación y envío puntual de informes de casos de seguridad (Individual Case Safety Reports) relacionados con medicamentos. Estos informes son fundamentales para proteger la salud de los pacientes.",
      },
      {
        q: "¿Gestionáis tanto SUSAR como ICSR?",
        a: "Sí, gestionamos tanto las Reacciones Adversas Graves e Inesperadas Sospechadas (SUSAR) durante los ensayos clínicos como los ICSR para productos comercializados.",
      },
      {
        q: "¿Qué incluye el servicio de XEVMPD?",
        a: "Nuestro servicio de XEVMPD incluye la introducción y validación de datos, la gestión de prerrequisitos, la notificación electrónica, el soporte de autorización de comercialización y las actualizaciones regulatorias.",
      },
    ],
    whatTitle: "¿Qué es la gestión de ICSR?",
    whatParagraphs: [
      "La gestión de ICSR abarca la <strong class='text-foreground'>recopilación, evaluación y envío puntual de informes de casos de seguridad</strong> relacionados con medicamentos. Estos informes desempeñan un papel fundamental en la protección de la salud de los pacientes al identificar y evaluar reacciones adversas.",
      "Ya se trate de SUSAR (Reacciones Adversas Graves e Inesperadas Sospechadas) durante ensayos clínicos o de ICSR (Informes de Casos Individuales de Seguridad) para productos comercializados, ofrecemos una solución fluida y conforme a la normativa, adaptada a tus necesidades.",
      "Nuestro enfoque garantiza el cumplimiento de los estándares regulatorios globales y proporciona un proceso eficiente y transparente para la gestión de datos de seguridad. Componentes clave de la gestión de ICSR:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Recepción y triaje de casos</strong>: evaluación rápida y categorización de informes entrantes para priorizar los casos críticos.</li><li><strong>Entrada y validación de datos</strong>: garantía de que todos los campos de datos se capturen y validen correctamente para la notificación regulatoria.</li><li><strong>Revisión médica</strong>: análisis experto de eventos adversos para determinar causalidad y gravedad.</li><li><strong>Envíos regulatorios</strong>: notificación puntual de casos a las autoridades, cumpliendo estándares locales e internacionales (p. ej., EudraVigilance, FDA).</li><li><strong>Archivo y seguimiento</strong>: documentación exhaustiva para mantener la trazabilidad y garantizar la preparación para auditorías.</li><li><strong>Servicio XEVMPD (EudraVigilance Medicinal Product Dictionary)</strong>: entrada y validación de datos, gestión de prerrequisitos, notificación electrónica, soporte de autorización de comercialización y actualizaciones regulatorias.</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial la gestión de ICSR?",
    whyBenefits: [
      { title: "Cumplimiento regulatorio", text: "Evita sanciones, retrasos o retiradas de productos cumpliendo con los requisitos de farmacovigilancia." },
      { title: "Mitigación de riesgos", text: "La identificación temprana de señales de seguridad ayuda a prevenir la escalada a problemas más graves." },
      { title: "Acceso al mercado", text: "Genera confianza en los stakeholders con sistemas robustos de gestión de seguridad." },
      { title: "Eficiencia operativa", text: "Optimiza procesos para reducir cargas administrativas y centrarte en tus objetivos principales." },
      { title: "Transparencia de datos", text: "Contribuye a un sistema de farmacovigilancia robusto que protege la salud pública." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "Los servicios de gestión de ICSR de QbD Group están diseñados para simplificar y reforzar tus esfuerzos de farmacovigilancia:",
      cards: [
        { title: "Gestión integral de casos", text: "Desde SUSAR durante ensayos clínicos hasta ICSR para productos comercializados." },
        { title: "Equipo experto", text: "Profesionales cualificados con profundo conocimiento de los requisitos regulatorios globales." },
        { title: "Soluciones de principio a fin", text: "Soporte para recepción de casos, procesamiento, revisión médica y envíos regulatorios." },
        { title: "Garantía de cumplimiento", text: "Procesos alineados con las directrices de la EMA, la FDA y la ICH." },
        { title: "Enfoque personalizado", text: "Flujos de trabajo a medida que se adaptan a tus necesidades operativas y regulatorias." },
        { title: "Gestión integral de datos", text: "Entrada y validación precisas de datos de productos medicinales en el XEVMPD." },
      ],
    },
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Colaborar con QbD Group significa:",
    partnerBullets: [
      "Perspectiva global — la experiencia de nuestro equipo abarca mercados internacionales, garantizando el cumplimiento allí donde operes.",
      "Soluciones a medida — diseñamos procesos que se alinean con tus necesidades y objetivos específicos.",
      "Trayectoria demostrada — décadas de experiencia en servicios de vigilancia con un enfoque en calidad y fiabilidad.",
      "Soporte de principio a fin — desde la supervisión de seguridad en ensayos clínicos hasta la farmacovigilancia post-comercialización.",
    ],
  },

  /* ───── Farmacovigilancia y formación combinada certificada ───── */
  "pharmacovigilance-certified-blended-training": {
    title: "Farmacovigilancia y formación combinada certificada",
    subtitle: "Capacita a tu equipo con formación eficiente y conforme en farmacovigilancia",
    description:
      "En el mundo en constante evolución de la farmacovigilancia, asegurar que tu equipo esté bien formado y cumpla con las regulaciones nacionales e internacionales es fundamental. Ofrecemos cursos a medida y una plataforma de formación online diseñada para elevar los estándares de cumplimiento.",
    faqItems: [
      {
        q: "¿Qué tipo de formación en farmacovigilancia ofrecéis?",
        a: "Ofrecemos programas de formación combinada que combinan módulos de e-learning online con sesiones presenciales prácticas, todo ello certificado según los más altos estándares internacionales de farmacovigilancia.",
      },
      {
        q: "¿Se pueden personalizar los cursos?",
        a: "Sí, diseñamos cursos a medida alineados con los requisitos operativos y regulatorios específicos de tu organización.",
      },
      {
        q: "¿La formación está certificada?",
        a: "Sí, nuestros programas de formación cumplen los más altos estándares internacionales de farmacovigilancia y proporcionan certificación reconocida.",
      },
    ],
    whatTitle: "¿Qué es la formación combinada certificada en farmacovigilancia?",
    whatParagraphs: [
      "La formación en farmacovigilancia combina <strong class='text-foreground'>conocimientos especializados con métodos de impartición flexibles</strong> para satisfacer las necesidades de tu organización. Combinando cursos online y presenciales, tu equipo recibe la formación de mayor calidad en el formato que mejor se adapte.",
      "Características principales de la formación en farmacovigilancia:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Cursos a medida</strong>: contenido personalizado alineado con tus requisitos operativos y regulatorios específicos.</li><li><strong>Plataforma de formación online</strong>: soluciones flexibles de e-learning que permiten a tu equipo aprender a su propio ritmo.</li><li><strong>Programas certificados</strong>: formación que cumple los más altos estándares internacionales de farmacovigilancia.</li><li><strong>Aprendizaje combinado</strong>: una combinación de sesiones online y presenciales para un conocimiento integral y práctico.</li><li><strong>Formadores expertos</strong>: instrucción impartida por profesionales de la industria con años de experiencia práctica en farmacovigilancia.</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial la formación en farmacovigilancia?",
    whyBenefits: [
      { title: "Cumplimiento regulatorio", text: "Mantente al día con los requisitos de farmacovigilancia de la EMA, la FDA y otros organismos internacionales." },
      { title: "Mitigación de riesgos", text: "Prepara a tu equipo para identificar y gestionar riesgos de seguridad de forma proactiva." },
      { title: "Excelencia operativa", text: "Optimiza flujos de trabajo y mejora la eficiencia con personal bien formado." },
      { title: "Equipos empoderados", text: "Proporciona a tus empleados la confianza y la experiencia necesarias para destacar en actividades de farmacovigilancia." },
      { title: "Aprendizaje continuo", text: "Mantén el ritmo con la evolución de las regulaciones y las mejores prácticas de la industria." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "Los servicios de formación combinada certificada en farmacovigilancia de QbD Group proporcionan soluciones de aprendizaje integrales diseñadas para tus necesidades específicas:",
      cards: [
        { title: "Diseño de cursos a medida", text: "Contenido formativo personalizado para abordar los retos únicos de tu organización." },
        { title: "Plataforma online", text: "Acceso fácil a módulos de e-learning que cubren temas esenciales de farmacovigilancia." },
        { title: "Talleres presenciales", text: "Sesiones prácticas que proporcionan conocimientos aplicables del mundo real." },
        { title: "Certificación", text: "Programas que aseguran que tu equipo cumple los estándares internacionales de farmacovigilancia." },
        { title: "Soporte continuo", text: "Actualizaciones continuas y acceso a recursos de formación para mantener el cumplimiento." },
      ],
    },
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Elegir QbD Group para la formación en farmacovigilancia proporciona:",
    partnerBullets: [
      "Experiencia en la industria — más de una década de experiencia en farmacovigilancia y cumplimiento regulatorio.",
      "Metodologías probadas — programas de formación diseñados para obtener resultados medibles.",
      "Alcance global — experiencia en el cumplimiento de requisitos de formación internacionales y locales.",
      "Aprendizaje flexible — formatos de formación combinada adaptados a las preferencias y horarios de tu equipo.",
      "Equipos empoderados — compromiso de garantizar que tu personal esté preparado para destacar en las operaciones de farmacovigilancia.",
    ],
  },
};

export const getPvOfferingEs = (contentKey: string): PvOfferingI18n | undefined =>
  pvOfferingsEs[contentKey];

/* ───────────────────────────────────────────────────
   Simplified Chinese (zh-cn) translations.
   ─────────────────────────────────────────────────── */
const pvOfferingsZh: Record<string, PvOfferingI18n> = {
  "comprehensive-medical-information-services": {
    title: "全面医学信息服务",
    subtitle: "在关键时刻提供清晰、准确的回复",
    description:
      "准确且及时的医学信息不仅是法规要求 — 更是患者安全与产品信赖的重要组成部分。通过我们的医学信息服务，我们确保与您的相关方进行清晰、准确的沟通。",
    whatTitle: "为什么医学信息如此重要？",
    whatParagraphs: [
      "医学信息在<strong class='text-foreground'>保障患者安全、维持法规合规以及与医疗专业人员和公众建立信任</strong>方面发挥着至关重要的作用。",
      "我们的核心服务包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>咨询处理</strong>：以准确、合规且具有科学依据的信息回复来自医疗专业人员、患者或公众的医学问题。</li><li><strong>不良事件与产品投诉分类</strong>：识别、记录并将潜在的安全问题或产品投诉上报至相应的 Pharmacovigilance 或质量团队。</li><li><strong>多语种医学信息呼叫中心</strong>：由经过专业培训的人员组成的专属电话、电子邮件和数字化支持渠道，用于处理来电咨询。</li><li><strong>产品支持项目</strong>：专为复杂疗法设计，旨在弥合处方与正确使用之间的差距，为患者提供个性化、持续的教育和支持 — 增强品牌信任并提升长期治疗成效。</li><li><strong>文献监测</strong>：系统性审阅同行评审期刊、临床出版物和科学数据库，识别有关产品疗效、安全特征、超说明书使用及前沿研究的相关更新 — 保持回复文档和法规递交资料的时效性。</li></ul>",
    ],
    whyTitle: "为什么医学信息必不可少？",
    whyBenefits: [
      { title: "支持安全的产品使用", text: "清晰、科学准确的信息有助于医疗服务提供者和患者正确使用药物，最大程度降低误用或不良反应的风险。" },
      { title: "确保法规合规", text: "稳健的医学信息体系有助于满足本地及国际要求，保护您的组织免受合规风险。" },
      { title: "建立信任与公信力", text: "对医学咨询的及时、透明回复可在医疗专业人员、患者和相关方之间建立信心。" },
      { title: "强化 Pharmacovigilance", text: "高效的医学信息团队有助于及早发现安全信号，并促进规范的不良事件报告。" },
      { title: "改善患者体验", text: "提供清晰、富有同理心且个性化的医学支持，让患者在整个治疗过程中感受到陪伴 — 建立信任、提升依从性，确保他们在每一步都得到支持。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "在 QbD Group，我们超越基础服务，提供可根据您的需求灵活调整并与您的组织共同成长的定制化医学信息解决方案：",
      cards: [
        { title: "全渠道定制化支持", text: "灵活的服务可满足您的具体需求，无论是 24/7 全天候支持还是标准工作时间内的支持。" },
        { title: "可扩展的解决方案", text: "我们同时提供全方位服务和补充资源，确保在高峰期或突发情况下服务不中断。" },
        { title: "先进技术整合", text: "通过运用前沿技术简化咨询管理，确保回复始终如一且高质量。" },
        { title: "多语种专业能力", text: "我们的团队精通多种语言，能够在不同地区提供具有文化敏感性的支持。" },
        { title: "对质量与合规的承诺", text: "作为 Pharmacovigilance 专家，我们优先识别和报告产品质量问题及不良事件，确保符合所有相关法规。" },
        { title: "量身定制的产品支持项目", text: "就用药指导、副作用管理、储存要求和依从策略提供个性化、持续的教育与支持。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 提供医学信息服务，将获得：",
    partnerBullets: [
      "专业知识 — 在 Pharmacovigilance 和法规合规方面拥有数十年经验。",
      "创新方法 — 采用 PSO/ACO 等先进方法论，实现卓越的安全数据分析。",
      "无缝服务 — 从数据收集到法规递交的端到端支持。",
      "成熟业绩 — 为各类产品和市场成功交付安全报告。",
    ],
  },

  /* ───── ICSRs Management ───── */
  "icsr-management": {
    title: "ICSR 管理",
    subtitle: "为您的临床试验和已上市产品简化安全报告流程",
    description:
      "有效管理安全病例报告是 Pharmacovigilance 与临床试验监督的基石。在 QbD Group，我们的专业 ICSR 管理服务可确保安全信息的及时收集、处理与上报。",
    faqItems: [
      {
        q: "什么是 ICSR 管理？",
        a: "ICSR 管理涵盖与药品相关的个例安全报告（Individual Case Safety Reports）的收集、评估和及时递交。这些报告对于保护患者健康至关重要。",
      },
      {
        q: "你们是否同时处理 SUSAR 和 ICSR？",
        a: "是的，我们既处理临床试验期间的可疑非预期严重不良反应（SUSAR），也处理已上市产品的 ICSR。",
      },
      {
        q: "XEVMPD 服务包含哪些内容？",
        a: "我们的 XEVMPD 服务包括数据录入与验证、前置条件管理、电子报告、上市许可支持以及法规更新。",
      },
    ],
    whatTitle: "什么是 ICSR 管理？",
    whatParagraphs: [
      "ICSR 管理涵盖与药品相关的<strong class='text-foreground'>安全病例报告的收集、评估和及时递交</strong>。这些报告通过识别和评估不良反应，在保护患者健康方面发挥着至关重要的作用。",
      "无论是临床试验期间的 SUSAR（可疑非预期严重不良反应），还是已上市产品的 ICSR（个例安全报告），我们都能提供顺畅且合规的解决方案，以满足您的需求。",
      "我们的方法可确保符合全球法规标准，并为安全数据管理提供高效、透明的流程。ICSR 管理的关键要素：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>病例接收与分类</strong>：对收到的报告进行快速评估和分类，以优先处理重要病例。</li><li><strong>数据录入与验证</strong>：确保所有数据字段都被准确捕获并验证，以用于法规报告。</li><li><strong>医学审核</strong>：由专家对不良事件进行分析，以判定因果关系与严重程度。</li><li><strong>法规递交</strong>：及时向监管机构报告病例，符合本地与国际标准（例如 EudraVigilance、FDA）。</li><li><strong>归档与跟踪</strong>：完整的文档记录，以保持可追溯性并确保审计就绪。</li><li><strong>XEVMPD（EudraVigilance Medicinal Product Dictionary）服务</strong>：数据录入与验证、前置条件管理、电子报告、上市许可支持以及法规更新。</li></ul>",
    ],
    whyTitle: "为什么 ICSR 管理至关重要？",
    whyBenefits: [
      { title: "法规合规", text: "通过满足 Pharmacovigilance 要求，避免处罚、延误或产品撤回。" },
      { title: "风险缓解", text: "及早识别安全信号，有助于防止其升级为更严重的问题。" },
      { title: "市场准入", text: "通过稳健的安全管理体系赢得相关方的信任。" },
      { title: "运营效率", text: "优化流程以减少行政负担，让您专注于核心目标。" },
      { title: "数据透明", text: "助力构建强大的 Pharmacovigilance 体系，保护公众健康。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "QbD Group 的 ICSR 管理服务旨在简化并强化您的 Pharmacovigilance 工作：",
      cards: [
        { title: "全面的病例管理", text: "从临床试验期间的 SUSAR 到已上市产品的 ICSR。" },
        { title: "专家团队", text: "具备全球法规要求深厚知识的合格专业人员。" },
        { title: "端到端解决方案", text: "为病例接收、处理、医学审核和法规递交提供支持。" },
        { title: "合规保障", text: "流程与 EMA、FDA 和 ICH 指南保持一致。" },
        { title: "定制化方法", text: "量身定制的工作流程，以适应您的运营和法规需求。" },
        { title: "全面的数据管理", text: "在 XEVMPD 中准确录入与验证药品数据。" },
      ],
    },
    partnerTitle: "为何选择 QbD Group？",
    partnerIntro: "与 QbD Group 合作意味着：",
    partnerBullets: [
      "全球视野 — 我们团队的专业能力覆盖国际市场，确保您在任何运营地都能合规。",
      "量身定制的解决方案 — 我们设计的流程与您的具体需求和目标保持一致。",
      "经过验证的业绩 — 数十年的 Vigilance 服务经验，专注于质量与可靠性。",
      "端到端支持 — 从临床试验的安全监督到上市后 Pharmacovigilance。",
    ],
  },

  /* ───── PV Audits & Mock Inspections ───── */
  "pv-audits-inspections-quality-system-management": {
    title: "Pharmacovigilance（PV）审计",
    subtitle: "凭借专业的 Pharmacovigilance 审计，确保合规与质量",
    description:
      "在 QbD Group，我们提供经过认证的 PV 审计、模拟检查以及与 ISO 相关的活动，以确保您的 Pharmacovigilance 体系符合 Good Pharmacovigilance Practices（GPvP）和国际质量标准。",
    whatTitle: "什么是 Pharmacovigilance 审计与模拟检查？",
    whatParagraphs: [
      "维护 Pharmacovigilance 的质量不仅是一项最佳实践 — 更是上市许可持有人（MAH）和临床试验申办者的基本义务。",
      "经过认证的 PV 审计和模拟检查可评估您的 <strong class='text-foreground'>Pharmacovigilance 体系对 GPvP 要求和 ISO 标准的合规性</strong>。这些活动有助于识别差距、改进流程，并为您的组织迎接真正的法规检查做好准备。",
      "主要服务包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>认证 PV 审计</strong>：对您的 Pharmacovigilance 体系、流程与文档进行全面评估。</li><li><strong>模拟检查</strong>：模拟法规检查，以测试就绪情况并应对潜在的合规风险。</li><li><strong>质量体系评估</strong>：评估并优化您基于 ISO 的 Pharmacovigilance 质量管理体系。</li><li><strong>合规差距分析</strong>：识别需要改进的领域，使其与 GPvP 和 ISO 标准保持一致。</li><li><strong>纠正与预防措施（CAPA）</strong>：基于审计发现，指导制定并实施有效的 CAPA 计划。</li></ul>",
    ],
    whyTitle: "为何 PV 审计与模拟检查至关重要？",
    whyBenefits: [
      { title: "法规合规", text: "自信地满足 GPvP、EMA、FDA 和 ISO 的要求。" },
      { title: "检查就绪", text: "通过经过测试的流程与文档，全面做好接受法规检查的准备。" },
      { title: "提升质量体系", text: "识别差距并实施解决方案，以增强 Pharmacovigilance 流程。" },
      { title: "风险缓解", text: "主动应对合规风险，避免代价高昂的法规发现项。" },
      { title: "运营效率", text: "简化流程，提升生产力，并保持高质量标准。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "在 QbD Group，我们提供量身定制的解决方案，确保您 Pharmacovigilance 体系的完整性：",
      cards: [
        { title: "认证 PV 审计", text: "开展全面审计，以评估对 GPvP 和 ISO 标准的合规性。" },
        { title: "模拟检查", text: "模拟真实场景下的检查，以识别并应对薄弱环节。" },
        { title: "ISO 质量体系优化", text: "确保您的 Pharmacovigilance 流程符合国际 ISO 标准。" },
        { title: "可执行的 CAPA 支持", text: "提供详细计划以解决发现项，并确保持续改进。" },
        { title: "专家指导", text: "凭借多年经验，提升您组织的检查就绪能力。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "与 QbD Group 合作，您将获得：",
    partnerBullets: [
      "认证专业能力 — 一支具备 GPvP 和 ISO 标准深厚知识的合格审计员团队。",
      "经过验证的方法论 — 全面、系统的审计与模拟检查方法。",
      "量身定制的解决方案 — 针对您独特挑战与流程的个性化支持。",
      "全球经验 — 数十年涵盖国际法规框架的专业经验。",
      "持续改进 — 持续支持您优化质量体系并确保合规。",
    ],
  },

  /* ───── Pharmacovigilance & Certified Blended Training ───── */
  "pharmacovigilance-certified-blended-training": {
    title: "Pharmacovigilance 与认证混合式培训",
    subtitle: "通过高效合规的 Pharmacovigilance 培训，赋能您的团队",
    description:
      "在快速演变的 Pharmacovigilance 领域，确保团队接受良好培训并遵守国家和国际法规至关重要。我们提供量身定制的课程和在线培训平台，旨在提升合规标准。",
    faqItems: [
      {
        q: "你们提供哪类 Pharmacovigilance 培训？",
        a: "我们提供混合式培训项目，将在线 e-learning 模块与现场实操课程相结合，所有内容均按照国际最高 Pharmacovigilance 标准进行认证。",
      },
      {
        q: "课程可以定制吗？",
        a: "可以，我们设计的课程将与您组织的特定运营与法规要求保持一致。",
      },
      {
        q: "培训是否经过认证？",
        a: "是的，我们的培训项目符合国际最高 Pharmacovigilance 标准，并提供受认可的证书。",
      },
    ],
    whatTitle: "什么是 Pharmacovigilance 认证混合式培训？",
    whatParagraphs: [
      "Pharmacovigilance 培训将<strong class='text-foreground'>专业知识与灵活的授课方式相结合</strong>，以满足您组织的需求。通过结合线上与线下课程，您的团队将以最适合的形式获得最高质量的培训。",
      "Pharmacovigilance 培训的主要特点：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>定制化课程</strong>：根据您具体的运营和法规要求量身定制的内容。</li><li><strong>在线培训平台</strong>：灵活的 e-learning 解决方案，让团队按照自己的节奏学习。</li><li><strong>认证项目</strong>：符合国际最高 Pharmacovigilance 标准的培训。</li><li><strong>混合式学习</strong>：线上与现场课程相结合，构建全面且可落地的知识体系。</li><li><strong>专家讲师</strong>：由具备多年 Pharmacovigilance 实战经验的行业专业人士授课。</li></ul>",
    ],
    whyTitle: "为何 Pharmacovigilance 培训不可或缺？",
    whyBenefits: [
      { title: "法规合规", text: "紧跟 EMA、FDA 及其他国际机构的 Pharmacovigilance 要求。" },
      { title: "风险缓解", text: "让团队具备主动识别和管理安全风险的能力。" },
      { title: "卓越运营", text: "通过良好培训的人员优化工作流程并提升效率。" },
      { title: "赋能团队", text: "为员工提供在 Pharmacovigilance 活动中脱颖而出所需的信心与专业能力。" },
      { title: "持续学习", text: "紧跟不断演变的法规和行业最佳实践。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "QbD Group 的 Pharmacovigilance 认证混合式培训服务，提供针对您具体需求设计的综合学习解决方案：",
      cards: [
        { title: "量身定制的课程设计", text: "为您组织的独特挑战量身打造的培训内容。" },
        { title: "在线平台", text: "便捷访问涵盖 Pharmacovigilance 关键主题的 e-learning 模块。" },
        { title: "现场工作坊", text: "提供可应用于真实场景的实操课程。" },
        { title: "证书认证", text: "确保您的团队满足国际 Pharmacovigilance 标准的培训项目。" },
        { title: "持续支持", text: "持续的内容更新和培训资源访问，以保持合规。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 提供 Pharmacovigilance 培训将获得：",
    partnerBullets: [
      "行业专业能力 — 在 Pharmacovigilance 与法规合规领域拥有逾十年经验。",
      "经过验证的方法论 — 旨在产生可衡量成果的培训项目。",
      "全球覆盖 — 在满足国际与本地培训要求方面的专业能力。",
      "灵活学习 — 根据团队偏好和日程定制的混合式培训形式。",
      "赋能团队 — 致力于确保您的员工能够在 Pharmacovigilance 运营中脱颖而出。",
    ],
  },

  /* ───── Safety Reporting & Signal Detection ───── */
  "aggregate-safety-reporting": {
    title: "安全性报告与信号检测",
    subtitle: "为您的需求量身定制的全面安全性报告",
    description:
      "安全性报告是 Pharmacovigilance 和上市后监测的关键组成部分。在 QbD Group，我们分析并交付符合全球法规要求的安全性报告。",
    whatTitle: "什么是安全性报告？",
    whatParagraphs: [
      "安全性报告是指<strong class='text-foreground'>定期从多种来源收集、分析并递交安全性数据</strong>。这些报告提供产品安全特征的整体视图，对于持续的法规合规与风险缓解至关重要。",
      "安全性报告的主要类型：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>IND 年度报告</strong>：记录在研药物临床试验中的安全性信息。</li><li><strong>临床研究报告（CSR）</strong>：临床试验数据的全面总结。</li><li><strong>新药申请（NDA）</strong>：为获得新药法规批准而递交的安全性数据。</li><li><strong>定期安全性更新报告（PSUR）/ 定期获益–风险评估报告（PBRER）</strong>：上市后的安全性评估。</li><li><strong>研发期间安全性更新报告（DSUR）</strong>：临床研发过程中的持续评估。</li><li><strong>信号检测</strong>：全面的信号管理 — 从数据收集到法规报告 — 提供可执行的洞察，以主动缓解风险。</li></ul>",
    ],
    whyTitle: "为什么安全性报告至关重要？",
    whyBenefits: [
      { title: "法规合规", text: "满足全球报告要求，避免处罚或延误。" },
      { title: "风险管理", text: "及早识别并应对新出现的安全信号。" },
      { title: "产品信心", text: "为相关方提供清晰的产品安全性与有效性证据。" },
      { title: "流程优化", text: "高效管理并递交全面的安全性数据。" },
      { title: "及时识别风险", text: "及早发现潜在安全问题，防止其升级并减少危害。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "QbD Group 的安全性报告服务运用先进方法论（包括 PSO/ACO 分类方法），提供精准且可执行的洞察：",
      cards: [
        { title: "报告编制与交付", text: "量身定制的 IND 年度报告、CSR、NDA、PSUR/PBRER 以及 DSUR，以满足您的法规需求。" },
        { title: "先进方法论", text: "运用 PSO/ACO 进行分类与规则发现，优化上市后监测。" },
        { title: "法规专业能力", text: "确保与 EMA、FDA 及其他全球监管机构保持一致。" },
        { title: "全面解决方案", text: "从数据分析到报告递交，我们管理整个流程。" },
        { title: "定制化工作流程", text: "与您运营目标契合的量身定制报告策略。" },
        { title: "专业风险管理", text: "评估并分类信号，以判定其临床意义。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 提供安全性报告服务，将获得：",
    partnerBullets: [
      "专业知识 — 在 Pharmacovigilance 与法规合规方面拥有数十年经验。",
      "创新方法 — 采用 PSO/ACO 等先进方法论，实现卓越的安全性数据分析。",
      "无缝服务 — 从数据收集到法规递交的端到端支持。",
      "成熟业绩 — 为各类产品和市场成功交付安全性报告。",
    ],
  },

  /* ───── Regulatory Intelligence ───── */
  "regulatory-intelligence": {
    title: "法规情报",
    subtitle: "凭借可执行的法规洞察保持领先",
    description:
      "在当今快速演变的法规环境中，遵守国际与本地法规至关重要。在 QbD Group，我们的法规情报服务为您提供应对复杂要求所需的工具与专业能力。",
    whatTitle: "什么是法规情报？",
    whatParagraphs: [
      "法规情报是指<strong class='text-foreground'>对法规信息进行系统化的识别、收集与分析</strong>，以确保合规并支持战略决策。这一过程使组织能够主动应对法规挑战，并与全球标准保持一致。",
      "法规情报的关键要素：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>法规监测</strong>：追踪国际与本地法规的更新与变化。</li><li><strong>安全措施识别</strong>：评估各项要求，以实施有效的风险缓解策略。</li><li><strong>市场专属洞察</strong>：根据特定市场的需求量身定制合规策略。</li><li><strong>数据分类</strong>：对法规信息进行组织，确保清晰可读、便于检索。</li><li><strong>合规支持</strong>：提供可执行的建议，以持续满足法律要求。</li></ul>",
    ],
    whyTitle: "为何法规情报至关重要？",
    whyBenefits: [
      { title: "主动合规", text: "提前应对法规更新，避免代价高昂的处罚或延误。" },
      { title: "风险缓解", text: "在潜在安全问题升级前及早识别并加以应对。" },
      { title: "战略决策", text: "获取可执行的洞察，为产品研发和市场进入提供支持。" },
      { title: "运营效率", text: "通过理解并预判法规要求来优化流程。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "QbD Group 的法规情报服务为您的需求量身打造全面的解决方案：",
      cards: [
        { title: "协作式方法", text: "与 QbD Regulatory 紧密合作，提供整体性的合规策略。" },
        { title: "法规追踪", text: "对国际与本地法规进行实时监测。" },
        { title: "风险评估", text: "识别并实施与法律要求相一致的安全措施。" },
        { title: "定制化洞察", text: "提供市场专属情报，以支持全球与本地运营。" },
        { title: "合规优化", text: "确保流程高效，持续符合法规标准。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 提供法规情报服务，将获得：",
    partnerBullets: [
      "专家协作 — 与 QbD Regulatory 合作，提供全面的合规解决方案。",
      "全球专业能力 — 数十年应对复杂国际法规的经验。",
      "量身定制策略 — 针对您特定运营需求设计的定制化解决方案。",
      "经过验证的成果 — 跨行业与跨市场的成功法规合规业绩记录。",
    ],
  },

  /* ───── Local QPPV Representative Services ───── */
  "qppv-local-representative": {
    title: "本地 QPPV 代表服务",
    subtitle: "确保 Pharmacovigilance 合规在各地区无缝运作",
    description:
      "在多个地区管理 Pharmacovigilance 职责可能颇为复杂。在 QbD Vigilance，我们提供量身定制的 QPPV 与本地代表服务，以满足欧洲、英国及其他地区 MAH 的特定法规需求。",
    whatTitle: "什么是本地 QPPV 代表服务？",
    whatParagraphs: [
      "QPPV（Qualified Person for Pharmacovigilance）的角色<strong class='text-foreground'>受欧盟和英国法规强制要求，用以监督药品的安全性</strong>。此外，部分国家还要求设立本地代表，在当地层面管理 Pharmacovigilance 活动。这些角色确保 MAH 持续履行各地区特定的 PV 义务。",
      "我们的主要服务包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>欧盟 QPPV 服务</strong>：提供一名集中的合资格联络人，监督 PV 体系并确保在欧盟范围内的合规。</li><li><strong>英国 QPPV 服务</strong>：满足英国 Pharmacovigilance 监督的特定要求。</li><li><strong>本地 PV 代表</strong>：提供针对特定地区的 PV 支持，以确保在当地市场的合规。</li><li><strong>24/7 全天候支持</strong>：为不良事件报告及法规咨询提供全天候支持。</li><li><strong>审计与检查就绪</strong>：为审计和法规检查做好您 PV 体系的准备。</li><li><strong>Pharmacovigilance 热线服务</strong>：全面的 24/7 全球 Pharmacovigilance 支持，为您量身定制 — 从 ADR 管理到法规合规。</li></ul>",
    ],
    whyTitle: "为什么 QPPV 与本地代表必不可少？",
    whyBenefits: [
      { title: "法规要求", text: "符合欧盟、英国及全球 Pharmacovigilance 法规。" },
      { title: "集中监督", text: "确保所有 PV 体系都能得到有效监控和管理。" },
      { title: "本地专业能力", text: "促进与当地卫生主管部门和相关方的沟通。" },
      { title: "风险缓解", text: "快速识别并解决安全问题，以保护患者与产品。" },
      { title: "全球覆盖", text: "在多个地区无缝开展 PV 运营。" },
      { title: "即时响应", text: "实时应对安全顾虑与不良事件，减少延误并缓解风险。" },
      { title: "运营连续性", text: "跨时区、跨地区保持顺畅的 Pharmacovigilance 运营。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "在 QbD Vigilance，我们提供满足国际与本地法规要求的全面本地 QPPV 服务：",
      cards: [
        { title: "专属的欧盟与英国 QPPV 服务", text: "监督您的 PV 体系，确保在欧洲与英国的合规。" },
        { title: "本地 PV 代表", text: "提供本地市场的专业能力，以应对特定地区的法规义务。" },
        { title: "24/7 不良事件管理", text: "确保及时向卫生主管部门报告并保持沟通。" },
        { title: "法规联络", text: "作为 PV 主管部门的主要联系人。" },
        { title: "检查就绪", text: "为您的组织做好接受审计与检查的准备，以确保合规。" },
        { title: "全天候支持", text: "持续可用，处理紧急的 Pharmacovigilance 需求。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "与 QbD Vigilance 合作意味着：",
    partnerBullets: [
      "全球专业能力 — 在欧洲、英国及其他国际市场管理 PV 职责的丰富经验。",
      "法规合规 — 专属的 QPPV 与本地代表确保完全遵循 PV 法规。",
      "量身定制的解决方案 — 满足您组织独特需求的个性化支持。",
      "经过验证的可靠性 — 受到全球 MAH 信赖的高质量 Pharmacovigilance 服务。",
      "24/7 支持 — 全天候提供不良事件报告与法规咨询的支持。",
    ],
  },

  /* ───── QMS & PSMF Services ───── */
  "qms-psmf-services": {
    title: "Pharmacovigilance QMS 与 PSMF 服务",
    subtitle: "通过高效的 Pharmacovigilance 体系简化合规",
    description:
      "满足国家与国际 Pharmacovigilance 法规要求需要稳健的体系和细致的文档记录。在 QbD Group，我们提供全面的 Pharmacovigilance QMS 以及 PSMF 支持。",
    whatTitle: "什么是 Pharmacovigilance QMS 与 PSMF？",
    whatParagraphs: [
      "Pharmacovigilance QMS 是一个<strong class='text-foreground'>结构化框架，旨在确保根据 Good Pharmacovigilance Practice（GPvP）指南有效管理 PV 活动</strong>。PSMF 是一份强制性文件，详细概述了上市许可持有人（MAH）的 PV 体系。无论您的组织尚未建立 PV 职能，还是能力有限，我们通过 PV 协议提供的“附加式”解决方案都能简化合规的实现。",
      "我们 QMS 与 PSMF 服务的主要特点：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>QMS 实施</strong>：建立量身定制以满足您 PV 需求的质量管理体系。</li><li><strong>PSMF 编制与维护</strong>：创建并更新您的 PSMF，确保符合 EMA、FDA 及其他法规主管部门的要求。</li><li><strong>法规对齐</strong>：确保您的体系符合国际 GPvP 标准。</li><li><strong>无缝整合</strong>：通过 PV 协议提供“附加式”解决方案，简化实施过程。</li><li><strong>持续支持</strong>：对您的 QMS 与 PSMF 进行持续维护与优化。</li></ul>",
    ],
    whyTitle: "为何 QMS 与 PSMF 对 Pharmacovigilance 至关重要？",
    whyBenefits: [
      { title: "法规合规", text: "满足全球对 PV 体系文档与管理的要求。" },
      { title: "运营效率", text: "简化流程，减少行政负担。" },
      { title: "风险缓解", text: "确保准确报告并及时发现安全信号。" },
      { title: "审计就绪", text: "保持文档的最新状态，以应对检查与审计。" },
      { title: "可扩展性", text: "适应不断演变的法规与组织需求。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "QbD Group 提供量身定制的解决方案，简化您的 Pharmacovigilance 合规需求：",
      cards: [
        { title: "全面的 QMS 建设", text: "建立符合国际标准的 PV 质量管理体系。" },
        { title: "PSMF 编制", text: "编写满足 EMA 与 FDA 要求的详细 Pharmacovigilance System Master Files。" },
        { title: "无缝的“附加式”解决方案", text: "通过 Pharmacovigilance 协议整合 QMS 与 PSMF 服务。" },
        { title: "持续维护", text: "定期更新，确保文档始终合规并随时可接受审计。" },
        { title: "专家指导", text: "凭借在 PV 运营与法规合规方面的丰富经验提供支持。" },
      ],
    },
    partnerTitle: "为何选择与 QbD Group 合作？",
    partnerIntro: "选择 QbD Group 提供 QMS 与 PSMF 服务，您将获得：",
    partnerBullets: [
      "法规专业能力 — 在 Pharmacovigilance 合规与体系设计方面拥有数十年经验。",
      "量身定制的解决方案 — 灵活的服务，专为满足您组织独特需求而设计。",
      "无缝整合 — 简化 QMS 与 PSMF 的实施，最大限度减少业务中断。",
      "全球覆盖 — 在满足国际与地区法规要求方面的专业能力。",
      "可靠支持 — 持续的协助，让您的体系保持高效与合规。",
    ],
  },
};

export const getPvOfferingZh = (contentKey: string): PvOfferingI18n | undefined =>
  pvOfferingsZh[contentKey];
